import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { z } from "zod";

import { languageStore } from "@/stores/preferences/language-store";

// Import the language store

import enUS from "./locales/en-US.json";
import nlNL from "./locales/nl-NL.json";
// Import custom zod locales
import enUSZod from "./zod-locales/en-US";
import nlNLZod from "./zod-locales/nl-NL";

export const defaultLanguage = "en-US";

export const defaultNamespace = "default";

export const resources = {
  "en-US": {
    [defaultNamespace]: enUS,
    "zod-custom": {
      invalid_phone: "Invalid phone number",
    },
  },
  "nl-NL": {
    [defaultNamespace]: nlNL,
    "zod-custom": {
      invalid_phone: "Ongeldig telefoonnummer",
    },
  },
};

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    defaultNS: defaultNamespace,
    ns: [defaultNamespace],
    resources,
    lng: defaultLanguage, // Always start with default language
    fallbackLng: defaultLanguage,
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
    interpolation: {
      escapeValue: false,
    },
  });

// Create custom error map that combines locale errors with custom translations
const createCustomErrorMap = (locale: "en-US" | "nl-NL"): z.core.$ZodErrorMap => {
  // Get the appropriate zod locale
  const zodLocale = locale === "nl-NL" ? nlNLZod() : enUSZod();
  const localeError = zodLocale.localeError;

  return (issue) => {
    // First check if there's a custom message from i18next
    if (issue.code === "custom" && issue.params?.i18n) {
      const customKey = issue.params.i18n as string;
      const customMessages = resources[locale]["zod-custom"] as Record<string, string>;
      const customMessage = customMessages[customKey];
      if (customMessage) {
        return { message: customMessage };
      }
    }

    // Fall back to locale error messages
    return localeError(issue);
  };
};

// Set initial locale
z.config({ customError: createCustomErrorMap(i18n.language as "en-US" | "nl-NL") });

// Update zod error map when language changes
i18n.on("languageChanged", (lng) => {
  z.config({ customError: createCustomErrorMap(lng as "en-US" | "nl-NL") });
});

// Function to initialize language from user data
export function initializeLanguageFromUser(userLanguage?: string) {
  if (userLanguage && userLanguage !== i18n.language) {
    languageStore.getState().initializeFromUser(userLanguage);
    i18n.changeLanguage(userLanguage);
  }
}

export { z };
